import { useRef, useState } from "react";

const SLUG_MAX = 40;

/**
 * Lowercase URL slug: spaces and other separators become dashes.
 * A trailing dash is kept while typing ("hello ") so the next word can continue;
 * pass `trim` on blur and save.
 */
export function slugify(raw: string, opts?: { trim?: boolean }): string {
  const folded = raw
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  let s = folded.replace(/[^a-z0-9]+/g, "-").replace(/^-+/, "");
  if (opts?.trim) s = s.replace(/-+$/g, "");
  if (s.length > SLUG_MAX) {
    s = s.slice(0, SLUG_MAX);
    if (opts?.trim) s = s.replace(/-+$/g, "");
  }
  return s;
}

export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9-]{2,40}$/.test(slug);
}

/** Slug field that follows a title until someone edits the slug itself. */
export function useSlug(initial = "") {
  const manual = useRef(slugify(initial, { trim: true }).length > 0);
  const [slug, setSlug] = useState(() => slugify(initial, { trim: true }));
  return {
    slug,
    setSlugInput(raw: string) {
      const next = slugify(raw);
      manual.current = slugify(next, { trim: true }).length > 0;
      setSlug(next);
    },
    blurSlug() {
      setSlug((current) => {
        const trimmed = slugify(current, { trim: true });
        if (!trimmed) manual.current = false;
        return trimmed;
      });
    },
    syncFrom(source: string) {
      if (manual.current) return;
      setSlug(slugify(source, { trim: true }));
    },
  };
}
